import LangEnum from '../../../../lang/LangEnum';
import { getOrderProductCombinationKey } from '../../../../utils/getOrderProductCombinationKey';
import { HTTP_STATUS } from '../../../../utils/httpStatus.const';
import {
  ListSortViewEnum,
  OrderProduct,
  SortView,
  UpdateItemFormat,
} from '../../../orders/products/orderProducts.types';
import { OfflineDatabaseSkuKit } from '../../database/offlineDatabase/types/offlineDatabaseKit.types';
import { createApiSauceResponse } from '../../utils/createApiSauceResponse.util';
import {
  MountKitGridItemsToUpdateParams,
  ValidateKitHasConflictsOnOrderProductsAndReturnErrorParams,
} from '../types/onlineOrderRepositoryUtils.types';
import { mountGridItemFromSkuKit } from './kitActions.utils';

export const validateKitHasConflictsOnOrderProductsAndReturnError = ({
  orderProductCombinationsMap,
  skuKits,
  kitId,
}: ValidateKitHasConflictsOnOrderProductsAndReturnErrorParams) => {
  const hasConflict = skuKits.some((skuKit) => {
    const combinationKey = getOrderProductCombinationKey({
      productColorId: skuKit.productColor.id,
      availability: skuKit.availability,
      deliveryWindowId: skuKit.deliveryWindow?.id ?? undefined,
    });
    const exists = orderProductCombinationsMap.has(combinationKey);

    return exists && orderProductCombinationsMap.get(combinationKey)?.kit?.id !== kitId;
  });

  if (hasConflict) {
    return createApiSauceResponse({
      ok: false,
      data: {
        status: HTTP_STATUS.BAD_REQUEST,
        message: 'alguns produtos do kit já estão no pedido',
        code: LangEnum.API_EXCEPTION_ORDER_ITEM_IN_KIT_IS_ALREADY_IN_ORDER,
      },
    });
  }

  return null;
};

export const mountKitGridItemsToUpdate = ({
  kitOrderProducts,
  skuKitsMap,
  kitQuantity,
}: MountKitGridItemsToUpdateParams) => {
  return kitOrderProducts.flatMap((orderProduct) => {
    const grid = orderProduct.grid.map((gridItem) => {
      const combinationKey = getOrderProductCombinationKey({
        productColorId: gridItem.sku.id,
        availability: orderProduct.availability,
        deliveryWindowId: orderProduct.deliveryWindow?.id ?? undefined,
      });

      const skuKit = skuKitsMap.get(combinationKey);

      const gridItemToUpdate: UpdateItemFormat = {
        id: gridItem.id,
        quantity: (skuKit && skuKit?.quantity * kitQuantity) || 0,
        availability: orderProduct.availability,
        skuId: gridItem.sku.id,
        productColorId: orderProduct.productColor.id,
        deliveryWindowId: orderProduct.deliveryWindow?.id ?? undefined,
      };
      return gridItemToUpdate;
    });

    return grid;
  });
};

export const mountKitGridItemsToCreate = (
  skuKits: OfflineDatabaseSkuKit[],
  kitQuantity: number = 1,
) => {
  const gridItems = skuKits.map((skuKit) => mountGridItemFromSkuKit(skuKit));

  const itemsToCreateOrUpdate = gridItems.map((gridItem) => {
    const updateOrCreateItem: UpdateItemFormat = {
      id: gridItem.id,
      availability: gridItem.availability,
      skuId: gridItem.skuId,
      quantity: gridItem.quantity * kitQuantity,
      productColorId: gridItem.productColorId,
      deliveryWindowId: gridItem.deliveryWindowId,
    };

    return updateOrCreateItem;
  });

  return itemsToCreateOrUpdate;
};

export const mountSkuKitsMap = (skuKits: OfflineDatabaseSkuKit[]) => {
  const skuKitsMap = new Map<string, OfflineDatabaseSkuKit>();

  skuKits.forEach((skuKit) => {
    const combinationKey = getOrderProductCombinationKey({
      productColorId: skuKit.sku.id,
      availability: skuKit.availability,
      deliveryWindowId: skuKit.deliveryWindow?.id ?? undefined,
    });

    skuKitsMap.set(combinationKey, skuKit);
  });

  return skuKitsMap;
};

const compareByReferenceCode = (a: OrderProduct, b: OrderProduct): number => {
  if (!a.referenceCode && !b.referenceCode) {
    return a.code.localeCompare(b.code);
  }
  if (!a.referenceCode || !b.referenceCode) {
    return a.referenceCode ? -1 : 1;
  }
  return a.referenceCode.localeCompare(b.referenceCode) || a.code.localeCompare(b.code);
};

export const sortOrderProducts = (
  orderProducts: OrderProduct[],
  sortBy?: SortView,
): OrderProduct[] => {
  switch (sortBy) {
    case ListSortViewEnum.CODE:
      return [...orderProducts].sort((a, b) => a.code.localeCompare(b.code));
    case ListSortViewEnum.PRODUCT_NAME_A_Z:
      return [...orderProducts].sort((a, b) => a.name.localeCompare(b.name));
    case ListSortViewEnum.REFERENCE_CODE:
      return [...orderProducts].sort(compareByReferenceCode);
    default:
      return orderProducts;
  }
};
